// Consolidated mock data for payments panels
// Combines all payment-related mock data previously scattered across multiple files

import type { PaymentInfo, PaymentDetail, Receipt } from "@shared/components/panels/types/panel-common.types";
import type { RiskCheck } from "@shared/types/risk.types";
import type { EventData } from '@shared/types/events.types';

// ========================================
// Payment Data
// ========================================

export const PAYMENTS_DATA: PaymentInfo[] = [
  {
    id: 'payment_001',
    amount: "USD 4,235.00",
    created: '2024-01-15T14:30:00Z',
    lastUpdate: "15 Jan 2024 14:30",
    customer: "acme@corp.com",
    paymentMethod: "Stripe",
    risk: "2",
    channel: "ECOM",
    status: "succeeded",
    statusDetails: {
      category: 'success',
      priority: 'low',
      canRefund: true
    }
  },
  {
    id: 'payment_002',
    amount: "USD 2,150.50",
    created: '2024-01-14T09:22:00Z',
    lastUpdate: "14 Jan 2024 09:22",
    customer: "contact@techsolutions.com",
    paymentMethod: "PayPal",
    risk: "1",
    channel: "API",
    status: "processing",
    statusDetails: {
      category: 'processing',
      priority: 'medium',
      requiresAction: false
    }
  },
  {
    id: 'payment_003',
    amount: "USD 875.25",
    created: '2024-01-13T16:45:00Z',
    lastUpdate: "13 Jan 2024 16:45",
    customer: "info@globalservices.com",
    paymentMethod: "Bank Transfer",
    risk: "5",
    channel: "POS",
    status: "declined",
    statusDetails: {
      category: 'failed',
      priority: 'medium',
      canRetry: true,
      reason: "Insufficient funds"
    }
  },
  {
    id: 'payment_004',
    amount: "USD 750.50",
    created: '2024-01-07T12:15:00Z',
    lastUpdate: "7 Jan 2024 12:15",
    customer: "payments@consultingfirm.com",
    paymentMethod: "Credit Card",
    risk: "9",
    channel: "ECOM",
    status: "blocked",
    statusDetails: {
      category: 'failed',
      priority: 'high',
      requiresAction: true,
      reason: "Flagged by fraud prevention system"
    }
  },
  {
    id: 'payment_005',
    amount: "USD 1,250.00",
    created: '2024-09-24T12:41:00Z',
    lastUpdate: "24 Sep 2024 12:41",
    customer: "underreview@example.com",
    paymentMethod: "Credit Card",
    risk: "9",
    channel: "ECOM",
    status: "under_review",
    statusDetails: {
      category: 'warning',
      priority: 'high',
      requiresAction: true,
      reason: "Payment flagged for manual review due to high risk score"
    }
  },
  {
    id: 'payment_006',
    amount: "USD 3,200.00",
    created: '2024-09-18T14:20:00Z',
    lastUpdate: "18 Sep 2024 14:20",
    customer: "chargeback@example.com",
    paymentMethod: "Visa",
    risk: "7",
    channel: "ECOM",
    status: "chargeback",
    statusDetails: {
      category: 'dispute',
      priority: 'high',
      requiresAction: true,
      reason: "Chargeback initiated by cardholder's bank"
    }
  }
];

export const PAYMENT_INFO: PaymentInfo = PAYMENTS_DATA[0];

export const PAYMENT_DETAILS: PaymentDetail[] = [
  { label: 'Payment Reference', value: 'PAYREF7g82k1m3q9z4' },
  { label: 'Description', value: 'Monthly subscription fee' },
  { label: 'Statement Descriptor', value: 'BRIK*SUBSCRIPTION' },
  { label: 'Trx Reference', value: 'TRX-2024-09-28-001' },
  { label: 'Execution Date', value: '28 Sep 2024 18:00' },
  { label: 'Card Network Reference', value: 'MC 817289471294124' },
  { label: 'ARN', value: '68764873264527365423' },
  { label: 'Authorization Code', value: '647283' },
  { label: 'Response Code', value: '00 (Approved)' },
  { label: 'PAN Sequence Number', value: '1234' },
  { label: 'Retry', value: 'Single' },
  { label: 'Order', value: 'ORD-00842' },
  { label: 'Subscription', value: 'SUBC-00847' },
  { label: 'Payment Schedule', value: 'Monthly' },
  { label: 'Acquirer', value: 'Chase Bank' },
  { label: 'Organization', value: 'BrikLabs' },
  { label: 'Product Platform', value: 'Core Commerce' },
  { label: 'Program', value: 'Recurring Billing Program' },
  { label: 'Processor', value: 'Stripe' },
  { label: 'Merchant', value: 'BRIK US' },
  { label: 'Source', value: 'API via Hosted Page' },
];

export const CUSTOMER_DETAILS: PaymentDetail[] = [
  { label: 'Account', value: 'user_9847abx52q' },
  { label: 'Name', value: 'John Doe' },
  { label: 'Email', value: 'johndoe@example.com' },
  { label: 'Shipping address', value: '123 Main St, New York, NY 10001, United States' },
  { label: 'Risk', value: '4/50 (Low Risk)' },
  { label: 'Last activity', value: '28 Sep 2024 18:00' }
];

export const PAYMENT_TIMELINE_EVENTS_DATA = [
  {
    id: 'payment-successful',
    title: 'Payment successful',
    timestamp: '24 Sep 2024 12:41',
  },
  {
    id: 'recurring-payments',
    title: 'Set for recurring payments',
    timestamp: '24 Sep 2024 12:41',
  },
  {
    id: '3ds-auth',
    title: '3DS Authentication',
    timestamp: '24 Sep 2024 12:41',
    details: [
      { action: 'Challenge initiated', timestamp: '24 Sep 2024 12:40' },
      { action: 'Customer authenticated', timestamp: '24 Sep 2024 12:40' },
      { action: 'Authentication successful', timestamp: '24 Sep 2024 12:41' }
    ]
  },
  {
    id: 'payment-processing',
    title: 'Payment Processing',
    timestamp: '24 Sep 2024 12:42',
  },
  {
    id: 'security-check',
    title: 'Security Check',
    timestamp: '24 Sep 2024 12:43',
    details: [
      { action: 'Risk assessment', timestamp: '24 Sep 2024 12:38' }
    ]
  },
  {
    id: 'payment-completed',
    title: 'Payment Completed',
    timestamp: '24 Sep 2024 12:44',
  },
  {
    id: 'payment-initiated',
    title: 'Payment initiated',
    timestamp: '24 Sep 2024 12:37',
  }
];

export const RISK_CHECKS: RiskCheck[] = [
  {
    status: 'error',
    title: 'Bank Verification',
    description: 'Bank verification failed due to insufficient funds or account restrictions. The issuing bank declined the transaction with response code 05 (Do Not Honor). This indicates potential account issues or spending limits that prevent processing.',
    score: 4,
    ruleId: 'block-1',
  },
  {
    status: 'success',
    title: '3DS Status and Details',
    description: '3D Secure authentication completed successfully. Customer was challenged and provided valid authentication through their bank\'s verification system. Payment authenticated with liability shift to issuer. Authentication method: SMS OTP verification.',
    score: 0,
    ruleId: 'auth-1',
  },
  {
    status: 'success',
    title: 'Postal Code Verification',
    description: 'Postal code verification passed successfully. The provided ZIP/postal code matches the billing address on file with the card issuer. AVS response: Z (ZIP matches, address does not match). This provides additional validation of cardholder identity.',
    score: 0,
    ruleId: 'block-5',
  },
  {
    status: 'success',
    title: 'Card Verification',
    description: 'Card verification value (CVV) check passed. The 3-digit security code provided matches the code on file with the issuing bank. Card is valid and not reported as lost, stolen, or fraudulent. Last 4 digits: 1234, expires 08/2026.',
    score: 0,
    ruleId: 'block-3',
  },
  {
    status: 'success',
    title: 'Address Verification',
    description: 'Address verification system (AVS) check completed with partial match. Street address matches billing records, but ZIP code verification returned a mismatch. This is common with recent moves or PO Box addresses. Risk level remains low.',
    score: 0,
    ruleId: 'block-4',
  }
];

export const RECEIPTS: Receipt[] = [
  {
    id: 'rcpt_1',
    name: 'Refund from farecloud',
    timestamp: '28 Sep 2024 15:30'
  },
  {
    id: 'rcpt_2',
    name: 'Refund from wordpress.com',
    timestamp: '27 Sep 2024 11:45'
  },
  {
    id: 'rcpt_3',
    name: 'Receipt for refund from company',
    timestamp: '26 Sep 2024 14:20'
  },
  {
    id: 'rcpt_4',
    name: 'Receipt for payment from company',
    timestamp: '25 Sep 2024 10:15'
  },
  {
    id: 'rcpt_5',
    name: 'Invoice from company',
    timestamp: '24 Sep 2024 09:30'
  }
];

export const FEES_DETAILS: PaymentDetail[] = [
  { label: 'Original amount', value: 'USD 1,680.00' },
  { label: 'Captured', value: 'USD 1,680.00' },
  { label: 'Refunded', value: 'USD 0.00' },
  { label: 'Tax', value: 'USD 134.40' },
  { label: 'Tip', value: 'USD 0.00' },
  { label: 'Surcharge', value: 'USD 0.00' },
  { label: 'Total fees', value: 'USD 28.70' },
  { label: 'Net accepted amount', value: 'USD 1,651.30' },
  { label: 'Net settlement amount (EUR)', value: '1,519.20 (1USD = 0.92) EUR' },
  { label: 'Refundable balance', value: 'USD 1,680.00' },
  { label: 'Acquirer fee', value: 'USD 11.76' },
  { label: 'Scheme fees', value: 'USD 3.46' },
  { label: 'Interchange', value: 'USD 18.48' },
  { label: 'Adjustments', value: 'USD 5.00' }
];

export const TAX_CALCULATION_DETAILS: PaymentDetail[] = [
  { label: 'Tax location', value: 'Albania' },
  { label: 'Tax amount', value: '$0.00 USD' },
  { label: 'Customer Tax ID', value: 'No Tax ID provided' },
  { 
    label: 'Taxability', 
    value: 'Not registered',
    showBadge: true,
    badgeVariant: 'destructive'
  },
  { label: 'Shipping address', value: 'No address' },
  { label: 'Customer billing address', value: 'Albania' },
  { label: 'Customer IP address', value: 'No IP address provided' }
];

export const PAYMENT_METHOD_DETAILS: PaymentDetail[] = [
  { label: 'Payment method', value: 'Card' },
  { label: 'Card number', value: '•••• •••• •••• 1234' },
  { label: 'Card type', value: 'Visa' },
  { label: 'Expiry date', value: '08/2026' },
  { label: 'Cardholder name', value: 'John Smith' },
  { label: 'Billing address', value: '123 Main St, New York, NY 10001' },
  { label: 'CVV verification', value: 'Passed' },
  { label: 'Address verification', value: 'Partial match' },
  { label: 'Issuing bank', value: 'Chase Bank' },
  { label: 'Card country', value: 'United States' }
];

export const PAYMENT_SECURITY_DETAILS: PaymentDetail[] = [
  { label: 'Postal Code', value: '10001' },
  { label: 'Origin Country', value: 'United States' },
  { label: 'AVS Check', value: '✓ Match' },
  { label: 'CVV/CVC Check', value: '✓ Match' },
  { label: '3DS', value: '✓ Successful' },
  { label: 'Limited Use', value: 'No' },
  { label: 'Allow Promo Codes', value: 'Yes' },
  { label: '3DS Retries', value: '0' },
  { label: 'Liability Shift', value: '✓ Yes, issuer accepted fraud risk' },
  { label: 'Merchant Requested 3DS', value: '✓ Yes (via Dynamic 3DS)' },
  { label: 'Dynamic 3DS Trigger', value: 'Default rule triggered: "Always propose 3DS"' },
  { label: 'Exemption', value: 'None' },
  { label: 'Exemption Reason', value: 'None' },
  { label: '3DS Version', value: 'None' },
];

export const PAYMENT_METHOD_TAB_DETAILS: PaymentDetail[] = [
  { label: 'ID', value: 'PMID_89b7x1ajhsd2q3', isCopyable: true },
  { label: 'Payment Method', value: 'Visa Credit Card' },
  { label: 'Cardholder', value: 'John Doe' },
  { label: 'Card Number', value: 'ending in 1234' },
  { label: 'Expires', value: '08/2026' },
  { label: 'Fingerprint', value: '7ac45d9f5b2e3a77', isCopyable: true },
  { label: 'Type', value: 'Credit' },
  { label: 'Issuer', value: 'Chase Bank (US)' },
  { label: 'Wallet', value: 'Apple Pay' },
  { label: 'Set Up For Future Use', value: 'Yes' },
  { label: 'Native Token', value: 'NTw121e23r32r23r', isCopyable: true },
  { label: 'Network Token', value: 'VS123428345359', isCopyable: true },
  { label: 'Entry Method', value: 'Manual' },
  { label: 'Currency', value: 'USD' },
  { label: 'Status', value: 'Default' },
];

// ========================================
// Events Data
// ========================================

// Sample events data for EventsLogsSection
// This will be replaced with real API data in the future

export const SAMPLE_EVENTS: EventData[] = [
  {
    id: 'evt_1',
    eventType: 'payment.authorized',
    description: 'Payment of $2,450.00 authorized successfully via Visa ending in 4242',
    timestamp: '12 Feb 2025 16:36',
    requestId: 'req_7k8m9n0p1q2r',
    status: 'approved'
  },
  {
    id: 'evt_2',
    eventType: 'fraud_check.completed',
    description: 'Risk assessment completed with score 0.15 - Low risk transaction',
    timestamp: '12 Feb 2025 16:36',
    requestId: 'req_3s4t5u6v7w8x',
    status: 'verified'
  },
  {
    id: 'evt_3',
    eventType: 'payment.captured',
    description: 'Payment captured and funds transferred to merchant account',
    timestamp: '12 Feb 2025 16:36',
    requestId: 'req_9y0z1a2b3c4d',
    status: 'succeeded'
  },
  {
    id: 'evt_4',
    eventType: 'webhook.sent',
    description: 'Payment confirmation webhook delivered to merchant endpoint',
    timestamp: '12 Feb 2025 16:36',
    requestId: 'req_5e6f7g8h9i0j',
    status: 'succeeded'
  },
  {
    id: 'evt_5',
    eventType: 'settlement.initiated',
    description: 'Settlement batch processing initiated for $2,450.00',
    timestamp: '12 Feb 2025 16:36',
    requestId: 'req_1k2l3m4n5o6p',
    status: 'processing'
  },
  {
    id: 'evt_6',
    eventType: 'customer.created',
    description: 'New customer profile created for john.doe@example.com',
    timestamp: '12 Feb 2025 16:36',
    requestId: 'req_7q8r9s0t1u2v',
    status: 'succeeded'
  },
  {
    id: 'evt_7',
    eventType: 'card.validated',
    description: 'Card validation completed for Visa **** 4242',
    timestamp: '12 Feb 2025 16:36',
    requestId: 'req_3w4x5y6z7a8b',
    status: 'verified'
  },
  {
    id: 'evt_8',
    eventType: 'payment.initiated',
    description: 'Payment request initiated for $2,450.00 from checkout session',
    timestamp: '12 Feb 2025 16:36',
    requestId: 'req_9c0d1e2f3g4h',
    status: 'pending'
  },
  {
    id: 'evt_9',
    eventType: 'error.processing',
    description: 'Transaction processing failed during payment authorization. AVS validation timeout from upstream processor. Customer used card ending in 4532 for $1,247.89 transaction.',
    timestamp: '12 Feb 2025 16:36',
    requestId: 'req_error_long_desc_123',
    status: 'failed'
  },
  {
    id: 'evt_10',
    eventType: 'compliance.check',
    description: 'KYC and AML compliance verification completed successfully. Customer John Smith verified through multiple data sources and approved for high-value transactions.',
    timestamp: '12 Feb 2025 16:36',
    requestId: 'req_compliance_detailed_456',
    status: 'approved'
  },
  {
    id: 'evt_11',
    eventType: 'webhook.retry',
    description: 'Webhook delivery retry #3 failed with HTTP 500 error. Merchant endpoint: https://api.merchant-example.com/payments/webhook',
    timestamp: '12 Feb 2025 16:36',
    requestId: 'req_webhook_retry_789',
    status: 'processing'
  }
];